import {
  Paper,
  Typography,
  alpha,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useState } from "react";
import { ACCENT, BROWN } from "../feature-configurator/utils/constants";

type BubbleProps = {
  isUser: boolean;
  children: React.ReactNode;
  label?: string;
  warning?: boolean;
  error?: boolean;
  onCopy?: () => Promise<void>;
  onReRequest?: () => void;
  showReRequest?: boolean;
};

export default function Bubble({
  isUser,
  children,
  label,
  warning,
  error,
  onCopy,
  onReRequest,
  showReRequest = false,
}: BubbleProps) {
  const [copied, setCopied] = useState(false);
  const [hovering, setHovering] = useState(false);

  const bg = isUser ? BROWN : "#fff";
  const fg = isUser ? "#fff" : "text.primary";
  const tone = error ? "error.main" : warning ? "warning.main" : fg;

  const handleCopy = async () => {
    if (onCopy) {
      await onCopy();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  

  const showActions = Boolean(onCopy || (showReRequest && onReRequest));

  return (
    <Paper
      elevation={0}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      sx={{
        position: "relative",
        maxWidth: 800,
        width: "fit-content",
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        color: tone,
        bgcolor: bg,
        border: `1px solid ${
          isUser ? alpha("#000", 0.18) : alpha("#000", 0.1)
        }`,
        boxShadow: isUser
          ? "0 8px 24px rgba(33,12,3,0.22)"
          : "0 8px 24px rgba(0,0,0,0.06)",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      {label ? (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 0.25,
            opacity: 0.7,
            color: isUser ? alpha("#fff", 0.9) : alpha(BROWN, 0.9),
            fontWeight: 700,
            letterSpacing: 0.15,
          }}
        >
          {label}
        </Typography>
      ) : null}

      {children}

      {/* Action toolbox – floats inside bottom-right */}
      {showActions && (
        <Stack
          direction="row"
          spacing={0.25}
          sx={{
            position: "absolute",
            right: 6,
            bottom: 6,
            bgcolor: isUser ? alpha("#000", 0.25) : alpha("#fff", 0.96),
            borderRadius: 999,
            border: `1px solid ${alpha("#000", 0.08)}`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            p: 0.25,
            opacity: hovering ? 1 : 0,
            pointerEvents: hovering ? "auto" : "none",
            transition: "opacity 120ms ease-in-out",
          }}
        >
          {onCopy && (
            <Tooltip title={copied ? "Copied!" : "Copy message"}>
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                  width: 24,
                  height: 24,
                  color: isUser ? "#fff" : "text.secondary",
                  "&:hover": {
                    bgcolor: isUser ? alpha("#fff", 0.18) : alpha(ACCENT, 0.12),
                  },
                }}
              >
                {copied ? (
                  <CheckIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Paper>
  );
}
